import { KpkFolder, KpkFolderTree } from "@keepeek/commons";
import { ChildFolder, Column, FolderTree, JSON, ParentFolder } from "@keepeek/refront-components";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { JsonConfig } from "../../../lib/commonPagePropsUtil";
import { getAdminConfigSectionData } from "../../../lib/config/customer-config-utils";
import logger from "../../../lib/logger-utils";
import {
  KThematicWidget,
  KThematicWidgetColumn1,
  KThematicWidgetColumn2,
  KThematicWidgetColumn3,
  KThematicWidgetColumn4,
  TranslationSchema,
} from "../../../models/configuration/components/kThematicWidget";
import { Type } from "../../../models/configuration/definitions/widget";

import { KThematicWidgetProps } from "./index";

export function getThematicConfiguration(
  customerSectionConfiguration: JsonConfig[],
  widgetId: string,
): KThematicWidgetProps | undefined {
  const thematics = getAdminConfigSectionData<KThematicWidget[]>(
    customerSectionConfiguration,
    CONFIGURATION_SECTION_PATH.COMPONENTS_K_THEMATIC_WIDGET,
  );

  if (thematics?.length === undefined || thematics.length === 0) {
    logger.error(`thematics est undefined ou vide`);
    return;
  }

  const thematic = thematics?.find((m) => m.id === widgetId);
  if (!thematic) {
    logger.error(`impossible de trouver le widget avec le widget id ${widgetId}`);
    return;
  }

  return {
    backgroundColor: thematic.backgroundColor,
    firstLevelFolderTextColor: thematic.firstLevelFolderTextColor,
    hoverTextColor: thematic.hoverTextColor,
    widgetId: thematic.id,
    kThematicWidgetColumn1: thematic.kThematicWidgetColumn1,
    kThematicWidgetColumn2: thematic.kThematicWidgetColumn2,
    kThematicWidgetColumn3: thematic.kThematicWidgetColumn3,
    kThematicWidgetColumn4: thematic.kThematicWidgetColumn4,
    rootFolderId: thematic.rootFolderId,
    secondLevelFolderTextColor: thematic.secondLevelFolderTextColor,
    title: thematic.title,
    borderRadius: undefined,
    type: Type.Thematic,
  };
}

function getAllFoldersWithFolderIds(
  folderIds: number[],
  foldersIdNameMap: Map<number, string>,
): ChildFolder[] {
  return folderIds.map(
    (folderId): ChildFolder => ({
      id: folderId,
      name: foldersIdNameMap.get(folderId)!,
    }),
  );
}

type KThematicWidgetColumn = {
  firstLevelFolderId?: number;
  folderIds?: number[];
};

function getColumn(
  columnIndex: number,
  kThematicWidgetColumn: KThematicWidgetColumn[],
  foldersIdNameMap: Map<number, string>,
): Column {
  const parentFolders: ParentFolder[] = kThematicWidgetColumn.map((col) => ({
    id: col.firstLevelFolderId as number,
    name: foldersIdNameMap.get(col.firstLevelFolderId as number) as string,
    folders: getAllFoldersWithFolderIds(col.folderIds as number[], foldersIdNameMap),
  }));

  return {
    columnIndex: columnIndex,
    parentFolders: parentFolders,
  };
}

export function getAdminJson(
  backgroundColor: string | undefined,
  firstLevelFolderTextColor: string | undefined,
  hoverTextColor: string | undefined,
  kThematicWidgetColumn1: KThematicWidgetColumn1[] | undefined,
  kThematicWidgetColumn2: KThematicWidgetColumn2[] | undefined,
  kThematicWidgetColumn3: KThematicWidgetColumn3[] | undefined,
  kThematicWidgetColumn4: KThematicWidgetColumn4[] | undefined,
  rootFolderId: number | null | undefined,
  secondLevelFolderTextColor: string | undefined,
  title: TranslationSchema[] | undefined,
  foldersIdNameMap: Map<number, string>,
): JSON {
  if (!rootFolderId) {
    rootFolderId = undefined;
  }

  const rootFolder: ChildFolder = {
    id: rootFolderId as number,
    name: rootFolderId ? (foldersIdNameMap.get(rootFolderId) as string) : "plan de classement",
  };

  const column1: Column = getColumn(
    1,
    kThematicWidgetColumn1 as KThematicWidgetColumn1[],
    foldersIdNameMap,
  );

  const column2: Column = getColumn(
    2,
    kThematicWidgetColumn2 as KThematicWidgetColumn2[],
    foldersIdNameMap,
  );

  const column3: Column = getColumn(
    3,
    kThematicWidgetColumn3 as KThematicWidgetColumn3[],
    foldersIdNameMap,
  );

  const column4: Column = getColumn(
    4,
    kThematicWidgetColumn4 as KThematicWidgetColumn4[],
    foldersIdNameMap,
  );

  const columns: Column[] = [column1, column2, column3, column4];

  return {
    rootFolder,
    columns,
    backgroundColor: backgroundColor ?? "",
    firstLevelFolderTextColor: firstLevelFolderTextColor ?? "",
    secondLevelFolderTextColor: secondLevelFolderTextColor ?? "",
    hoverTextColor: hoverTextColor ?? "",
    widgetTitle: title?.[0].value ?? "",
  };
}

function getChildFolderFromKpkFolders(kpkChildFolders: KpkFolder[]): ChildFolder[] {
  return kpkChildFolders.map((kpkChildFolder) => ({
    id: kpkChildFolder.id,
    name: kpkChildFolder.title ?? "",
  }));
}

function getParentFolderFromKpkFolderTree(firstLevelFolderTrees: KpkFolderTree[]): ParentFolder[] {
  return firstLevelFolderTrees.map((firstLevelFolderTree) => {
    const children = firstLevelFolderTree.children;

    const hasNoChildren =
      !firstLevelFolderTree.folder.hasChildren || !children || children.length === 0;

    return {
      id: firstLevelFolderTree.folder.id,
      name: firstLevelFolderTree.folder.title ?? "",
      folders: hasNoChildren ? [] : getChildFolderFromKpkFolders(children),
    };
  });
}

export function getBoFolderTree(
  rootFolderTree: { id: number | undefined; name: string } | KpkFolderTree,
  firstLevelFolderTrees: KpkFolderTree[],
): FolderTree {
  let id: number | undefined;
  let name: string;

  if ("folder" in rootFolderTree) {
    // C'est un KpkFolderTree
    id = rootFolderTree.folder.id;
    name = rootFolderTree.folder.title ?? "";
  } else {
    // C'est l'objet simple {id, name}
    id = rootFolderTree.id;
    name = rootFolderTree.name;
  }

  return {
    id,
    name,
    parentFolders: getParentFolderFromKpkFolderTree(firstLevelFolderTrees),
  };
}

export function getFoldersIdNameMap(boFolderTree: FolderTree): Map<number, string> {
  const foldersIdNameMap: Map<number, string> = new Map();

  boFolderTree.parentFolders.forEach((parentfolder: ParentFolder) => {
    foldersIdNameMap.set(parentfolder.id, parentfolder.name);

    if (!parentfolder.folders || parentfolder.folders.length < 1) {
      return;
    }

    parentfolder.folders.forEach((childFolder: ChildFolder) => {
      foldersIdNameMap.set(childFolder.id, childFolder.name);
    });
  });

  return foldersIdNameMap;
}
